import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
type Vertical = 'servicios' | 'inmobiliaria' | 'odonto' | 'educacion' | 'ecommerce' | 'estetica';
interface VerticalContent {
  label: string;
  clientMsg: string;
  aiTyping: string;
  aiReply: string;
  panel1: {
    k: string;
    v: string;
  }[];
  qualified: string;
  panel2Title: string;
  panel2Chips: string[];
  crmText: string;
  actionText: string;
  followupText: string;
}
const content: Record<Vertical, VerticalContent> = {
  servicios: {
    label: 'Servicios',
    clientMsg: 'Necesito una cotización para mi empresa.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, ¿qué servicio necesitas y para cuántas personas aproximadamente?',
    panel1: [{
      k: 'Servicio',
      v: 'Consultoría'
    }, {
      k: 'Tamaño',
      v: '20-50 personas'
    }],
    qualified: 'Intención: Alta',
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 10:00', 'Mañana · 15:00'],
    crmText: 'Lead actualizado',
    actionText: 'Cotización enviada',
    followupText: 'Seguimiento comercial activado'
  },
  inmobiliaria: {
    label: 'Inmobiliaria',
    clientMsg: 'Hola, vi el apartamento de 3 habitaciones. Quiero visitarlo.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo darte el precio, las características o agendar una visita.',
    panel1: [{
      k: 'Propiedad',
      v: 'Apto. 3 habitaciones'
    }, {
      k: 'Estado',
      v: 'Calificado'
    }],
    qualified: 'Intención: Alta',
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 11:00', 'Sábado · 10:00'],
    crmText: 'Nuevo lead registrado',
    actionText: 'Visita agendada',
    followupText: 'Seguimiento automático activado'
  },
  odonto: {
    label: 'Odontología',
    clientMsg: 'Hola, quiero información sobre diseño de sonrisa.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo ayudarte con información, disponibilidad y agendamiento.',
    panel1: [{
      k: 'Servicio',
      v: 'Diseño de sonrisa'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    qualified: 'Interés: Alto',
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 10:00', 'Mañana · 15:00'],
    crmText: 'Nuevo lead registrado',
    actionText: 'Cita agendada',
    followupText: 'Seguimiento automático activado'
  },
  educacion: {
    label: 'Educación',
    clientMsg: 'Quiero información sobre el programa.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo explicarte el programa, requisitos, duración y agendar una llamada.',
    panel1: [{
      k: 'Programa',
      v: 'Maestría en Marketing'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    qualified: 'Interés: Alto',
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 9:00', 'Jueves · 16:00'],
    crmText: 'Nuevo lead registrado',
    actionText: 'Llamada agendada',
    followupText: 'Seguimiento automático activado'
  },
  ecommerce: {
    label: 'E-commerce',
    clientMsg: '¿Tienen disponible este producto?',
    aiTyping: 'Consultando inventario...',
    aiReply: 'Sí, puedo ayudarte con disponibilidad, características y el proceso de compra.',
    panel1: [{
      k: 'Producto',
      v: 'Disponible'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    qualified: 'Intención: Compra directa',
    panel2Title: 'PROCESO DE COMPRA',
    panel2Chips: ['Pedido confirmado', 'Envío programado'],
    crmText: 'Cliente registrado',
    actionText: 'Compra confirmada',
    followupText: 'Seguimiento postventa activado'
  },
  estetica: {
    label: 'Estética',
    clientMsg: 'Quiero información sobre depilación láser.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo darte información sobre el servicio y consultar disponibilidad.',
    panel1: [{
      k: 'Servicio',
      v: 'Depilación láser'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    qualified: 'Interés: Alto',
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 11:00', 'Viernes · 16:00'],
    crmText: 'Nuevo lead registrado',
    actionText: 'Cita agendada',
    followupText: 'Seguimiento automático activado'
  }
};
const verticalOrder: Vertical[] = ['servicios', 'inmobiliaria', 'odonto', 'educacion', 'ecommerce', 'estetica'];

// message -> typing -> reply -> datos extraídos -> calificación -> panel2 -> CRM -> acción -> seguimiento
const STEP_DELAYS = [200, 1000, 1900, 2900, 3700, 4600, 5600, 6600, 7600];
export function DemoSection() {
  const [vertical, setVertical] = useState<Vertical>('servicios');
  const [step, setStep] = useState(0);
  const [playKey, setPlayKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    once: true,
    amount: 0.4
  });
  const data = content[vertical];
  useEffect(() => {
    if (!isInView) return;
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setStep(9);
      return;
    }
    const timers = STEP_DELAYS.map((delay, idx) => setTimeout(() => setStep(idx + 1), delay));
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isInView, playKey]);
  const replay = () => {
    setStep(0);
    setPlayKey(k => k + 1);
  };
  const selectVertical = (key: Vertical) => {
    if (key === vertical) return;
    setVertical(key);
    replay();
  };
  return <section id="como-funciona" className="bg-arena py-[64px] md:py-[96px]" data-id="element-85" data-mp="xvscu175llc2">
      <div className="max-w-[1000px] mx-auto px-6 md:px-20" data-id="element-86" data-mp="12tttygl0rakw">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="text-center max-w-[560px] mx-auto mb-3" data-id="element-87" data-mp="17l25pj9tn818">
          <span className="block font-body text-[11px] uppercase font-medium text-salvia tracking-[0.15em] mb-4" data-id="element-88" data-mp="2476ba15eqgb">
            ORIGEN EN ACCIÓN
          </span>
          <h2 className="font-display text-[32px] md:text-[44px] font-bold text-carbon leading-[1.1]" data-id="element-89" data-mp="bv1s6dahfo18">
            No te lo contamos. Te lo mostramos.
          </h2>
        </motion.div>

        {/* Pipeline label — sets the expectation before the demo plays */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 mb-10 text-carbon/35" data-id="element-90" data-mp="3fdbl31m87ado">
          {['MENSAJE', 'IA', 'DATOS', 'CALIFICACIÓN', 'CRM', 'ACCIÓN', 'SEGUIMIENTO'].map((stage, idx) => <span key={stage} className="flex items-center gap-1.5" data-id="element-91" data-mp="19gz6dyhtx77d" data-mpi={idx}>
              <span className="font-body text-[11px] uppercase tracking-[0.1em] font-medium" data-id="element-92" data-mp="14inwxovg4ypo">
                {stage}
              </span>
              {idx < 6 && <span className="text-[11px]" data-id="element-93" data-mp="1c5kzgg5hjhsx">→</span>}
            </span>)}
        </div>

        {/* Vertical toggle */}
        <div className="flex items-center justify-center gap-2 mb-3 flex-wrap px-2 -mx-2 overflow-x-auto" data-id="element-94" data-mp="1331wlw1p956o">
          {verticalOrder.map(key => <button key={key} onClick={() => selectVertical(key)} className={`font-body text-[13px] font-medium px-4 py-2.5 rounded-[6px] border transition-all duration-200 flex-shrink-0 ${vertical === key ? 'bg-verde text-arena border-verde' : 'bg-transparent text-carbon/60 border-carbon/15 hover:text-carbon hover:border-carbon/30'}`} data-id="element-95" data-mp="174f5kf039oht">
              {content[key].label}
            </button>)}
        </div>
        <p className="text-center font-body text-[12px] text-carbon/40 italic mb-12 max-w-[480px] mx-auto" data-id="element-96" data-mp="2iju1dfgkaabc">
          Ejemplos de aplicación — ORIGEN se adapta a distintos negocios con
          procesos de atención, calificación y agendamiento.
        </p>

        {/* Demo card */}
        <div ref={containerRef} className="bg-carbon rounded-[12px] p-6 md:p-10 max-w-[520px] mx-auto" data-id="element-97" data-mp="1k2utpjxvo02a">
          <div className="space-y-3 min-h-[420px]" data-id="element-98" data-mp="1f4ta8xwjhyqa">
            {/* Client message */}
            <motion.div animate={step >= 1 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-end" data-id="element-99" data-mp="2crsafimfxosx">
              <div className="bg-arena text-carbon font-body text-[14px] rounded-[12px] rounded-tr-none px-4 py-2.5 max-w-[85%]" data-id="element-100" data-mp="22sjguqvhmc73">
                {data.clientMsg}
              </div>
            </motion.div>

            {/* Typing indicator */}
            <motion.div animate={step === 2 ? {
            opacity: 1
          } : {
            opacity: 0
          }} transition={{
            duration: 0.25
          }} className="flex justify-start" data-id="element-101" data-mp="rc30mvc3zrj6">
              <div className="bg-verde/20 text-arena/70 font-body text-[12px] italic rounded-[12px] rounded-tl-none px-4 py-2" data-id="element-102" data-mp="2y0d50chgmr9r">
                {data.aiTyping}
              </div>
            </motion.div>

            {/* AI reply */}
            <motion.div animate={step >= 3 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-start -mt-2" data-id="element-103" data-mp="3crudqv82we6j">
              <div className="bg-verde text-arena font-body text-[14px] rounded-[12px] rounded-tl-none px-4 py-2.5 max-w-[85%]" data-id="element-104" data-mp="3lr929p4oqeoe">
                {data.aiReply}
              </div>
            </motion.div>

            {/* Datos extraídos */}
            <motion.div animate={step >= 4 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3 mt-4" data-id="element-105" data-mp="3vpf0nwra92oi">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-azul mb-1.5" data-id="element-106" data-mp="269x12tkfltxq">
                DATOS EXTRAÍDOS ✓
              </span>
              <div className="font-body text-[13px] text-arena/80 space-y-0.5" data-id="element-107" data-mp="2ba1hkjlcaony">
                {data.panel1.map(row => <p key={row.k} data-id="element-108" data-mp="2k9eiuozb86xl">
                    {row.k}: {row.v}
                  </p>)}
              </div>
            </motion.div>

            {/* Calificación */}
            <motion.div animate={step >= 5 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} data-id="element-109" data-mp="2u6sywnhbmtpd">
              <span className="font-body text-[12.5px] font-medium text-arena/85 flex items-center gap-2" data-id="element-110" data-mp="2yykp2wwkexez">
                LEAD CALIFICADO <span className="text-verde" data-id="element-111" data-mp="37yh9unfu3c4r">✓</span>
                <span className="text-arena/50" data-id="element-112" data-mp="37zsawfhwa5s6">· {data.qualified}</span>
              </span>
            </motion.div>

            {/* Panel 2 */}
            <motion.div animate={step >= 6 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3" data-id="element-113" data-mp="11a75356kmadn">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-salvia mb-2" data-id="element-114" data-mp="sabogku8xn68">
                {data.panel2Title} ✓
              </span>
              <div className="flex flex-wrap gap-2" data-id="element-115" data-mp="1k0ghf7w2nij2">
                {data.panel2Chips.map(chip => <span key={chip} className="font-body text-[12px] text-arena/80 bg-arena/10 rounded-full px-3 py-1" data-id="element-116" data-mp="1fyl75dw5xut5">
                    {chip}
                  </span>)}
              </div>
            </motion.div>

            {/* CRM */}
            <motion.div animate={step >= 7 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="pt-1" data-id="element-117" data-mp="23pqximzqc5hp">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-arena/40 mb-1" data-id="element-118" data-mp="2htxyp4jyvain">
                CRM
              </span>
              <span className="font-body text-[13px] font-medium text-arena/85 flex items-center gap-2" data-id="element-119" data-mp="2mvevoozbycty">
                {data.crmText} <span className="text-verde" data-id="element-120" data-mp="2d3hjpg0hzgu6">✓</span>
              </span>
            </motion.div>

            {/* Acción */}
            <motion.div animate={step >= 8 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} data-id="element-121" data-mp="1z3gj81o5abf8">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-arena/40 mb-1" data-id="element-122" data-mp="240ghhqer69vs">
                ACCIÓN
              </span>
              <span className="font-body text-[14px] font-medium text-arena flex items-center gap-2" data-id="element-123" data-mp="1u5epmmt3etpi">
                {data.actionText} <span className="text-verde" data-id="element-124" data-mp="3khuebu3htgj3">✓</span>
              </span>
            </motion.div>

            {/* Seguimiento */}
            <motion.div animate={step >= 9 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} data-id="element-125" data-mp="1cvat7mzinrdy">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-arena/40 mb-1" data-id="element-126" data-mp="18vacfvoy343q">
                SEGUIMIENTO
              </span>
              <span className="font-body text-[12.5px] text-arena/55 flex items-center gap-2" data-id="element-127" data-mp="1htvt1lauw91v">
                {data.followupText} <span className="text-verde" data-id="element-128" data-mp="1wi8tovkw2g2p">✓</span>
              </span>
            </motion.div>
          </div>

          <button onClick={replay} className="mt-6 inline-flex items-center gap-1.5 font-body text-[12px] text-arena/50 hover:text-arena/80 transition-opacity duration-150" data-id="element-129" data-mp="1rjfemypyeh0f">
            <RotateCcw size={12} data-id="element-130" data-mp="20levnr0yxl3d" /> Ver de nuevo
          </button>
        </div>
      </div>
    </section>;
}